import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ReactNode } from "react";
import { Archive, FilePlus2, FilterX, Search } from "lucide-react";
import { api } from "../../api/client";
import { formatMoney } from "../../lib/money";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Badge, StatusBadge } from "../../components/Badge";
import { ARCHIVE_REFUSED, archiveTabUrl, canArchive } from "./archiveRules";
import { useToast } from "../../components/Toast";
import { HeaderFilter } from "../../components/HeaderFilter";
import { DateRangeFilter } from "../../components/DateRangeFilter";
import { Pagination, pageCount, usePageSize } from "../../components/Pagination";
import type { DateRange } from "../../components/DateRangeFilter";
import { useAuth } from "../auth/useAuth";
import { ColumnPicker, FALLBACK_COLUMNS, normalizeColumns } from "./ColumnPicker";
import type { ListColumnSettings } from "./ColumnPicker";
import { CategoryPicker, categoryLabel } from "./CategoryPicker";
import { QUOTE_CATEGORIES_CHANGED, announceCategories } from "./quoteViews";
import type { ListCategorySettings } from "./CategoryPicker";
import { QUOTE_IS_READ_ONLY_FOR_ROLE, canWriteQuotes } from "../../api/types";
import type { Collection, Paginated, QuoteSummary } from "../../api/types";

// 報價單清單。搜尋打字停 300ms 才送,避免每個鍵都打後端。
//
// ── 表頭上的漏斗 ──────────────────────────────────────────────────
//
// 業主原話 2026-09-13:「再報價單列表的地方 我也要加上漏斗篩選」,他點名的四個
// 條件是客戶、報價日、狀態與負責人。四個漏斗各長在**它作用的那一欄**表頭上,
// 與品項庫的分類漏斗同一套(HeaderFilter);報價日是區間,走 DateRangeFilter。
//
// 標題一律讀後端的 available_columns,所以這一支連漏斗的名稱都不自己命名 ——
// 見下一段。
//
// **條件送給後端篩,不是在拿回來的那一頁上面篩。** 一頁只有 20 筆,在那 20 筆
// 上面篩的話,勾了某位同仁卻一筆都沒有,只因為第一頁剛好全是別人的單 ——
// 畫面上看起來就像那位同仁名下的單全部不見了。
//
// 漏斗的選項來自 `GET /api/quotes/filter-options`,**只有實際開過單的**客戶與
// 同仁。列出全部客戶會讓漏斗充滿從來沒開過單的名字,勾下去一定是空清單。
//
// ── 表頭不是寫死的 ────────────────────────────────────────────────
//
// 業主原話 2026-09-13:「/quote/quotes 這一頁 要顯示的表頭 我希望讓客戶自行指定
// 他想要顯示的列表」。所以要畫哪幾欄、照什麼順序,一律讀 `GET /api/users/me/list-columns` 的
// `quote_list_columns`(每個人一份,存在自己的帳號上)。
//
// **中文標題也跟著後端走。** 前端只認得欄位鍵(識別字),標題來自同一支 API 的
// `available_columns` —— 前端若自己再抄一份對照表,改了其中一邊就會出現
// 設定頁與表頭各叫各的名字。設定整個讀不到時退回 FALLBACK_COLUMNS,
// 這時表頭會直接顯示欄位鍵:看得出是系統出了問題,好過前端掰一組標題假裝正常。
//
// ── 封存 ──────────────────────────────────────────────────────────
//
// 業主原話 2026-09-13:「報價單>封存>封存內才可以刪除」。封存區是報價單底下的
// 一層,入口就在這一頁右上角,不是側邊欄多一個項目(側邊欄已經七項了)。
//
// 每一列的「封存」收在列尾的 ⋮ 裡,而且**對員工不是 disabled** —— 封存是全公司
// 都能做的(規格 §4:同仁自己清掉手上的死案子不必等老闆);老闆專屬的是刪除,
// 而刪除在封存區那一頁,不在這裡。
//
// **⋮ 不另開一欄,掛在最後一欄的列尾。** 要畫哪幾欄是老闆在「顯示欄位」裡決定的,
// 表上多一欄他沒有勾過的東西,那份設定就與畫面對不起來了。

// ── 分類 ──────────────────────────────────────────────────────────
//
// 業主 2026-10-05:「顯示欄位」旁邊多一個「分類」(草稿、已寄出、未結案、已結案、
// 未收款、已收款),**每個人一份、存在自己的帳號上**(`/api/users/me/list-categories`),
// 換裝置與重新登入都跟著人走。它與漏斗是「而且」:篩的是同一份清單。
//
// 與漏斗最大的差別是它**不寫進網址、也不被「清除篩選」洗掉** —— 漏斗是這一次
// 找東西用的暫時條件,分類是長久的偏好。為了不讓人看到空清單而困惑,
// 有分類在篩的時候,空清單的說明會點名它,並在旁邊給一顆「清除分類」。
// 分類的鍵與標題(以及誰看得到收款那兩個)全由後端決定,這一支不認得任何一個字。

const SEARCH_DEBOUNCE_MS = 300;

/**
 * 開單入口優先掛在這幾欄上,由前往後挑第一個有被勾起來的。
 *
 * 單號與專案名稱是**一定有值**的兩欄(後端必填);其餘可選欄位有一半是選填的,
 * 入口若落在那種欄位上,遇到空值就變成一顆看不見的按鈕。
 */
const OPEN_COLUMN_PREFERENCE = ["quote_no", "title"];

/** 這幾欄是金錢,右靠對齊、與下一列的位數對得起來。 */
const MONEY_COLUMNS = new Set(["subtotal", "tax_amount", "total"]);
/** 這幾欄是數字或日期,用等寬數字排版才不會一列一個寬度。 */
const TABULAR_COLUMNS = new Set([
  "quote_no",
  "quote_date",
  "subtotal",
  "tax_amount",
  "total",
  "version",
  "updated_at",
]);

/** 只填了日期選擇器、沒打那段自由文字時,用起訖日期湊出檔期。 */
function datesOf(quote: QuoteSummary): string {
  return [quote.event_start_date, quote.event_end_date].filter(Boolean).join(" - ");
}

/** 收款標籤的字與顏色。 */
const COLLECTION: Record<Collection, { label: string; tone: "danger" | "warn" | "ok" }> = {
  UNPAID: { label: "未收款", tone: "danger" },
  PARTIAL: { label: "部分收款", tone: "warn" },
  PAID: { label: "已收款", tone: "ok" },
};

/** 一格的內容。認不得的欄位鍵畫成空白格,不讓整張表垮掉。 */
function cellOf(key: string, quote: QuoteSummary): ReactNode {
  switch (key) {
    case "quote_no":
      return quote.quote_no;
    case "title":
      return quote.title;
    case "client":
      return quote.client_name;
    case "quote_date":
      return quote.quote_date;
    case "event":
      return quote.event_period || datesOf(quote);
    case "venue":
      return quote.venue ?? "";
    case "owner":
      return quote.owner_name ?? "";
    case "status":
      return (
        <span className="inline-flex items-center gap-1">
          <StatusBadge status={quote.status} />
          {/* 已結案的單再帶一格「申票中 / 已請票」(業主 2026-09-16)——
              業主要的是一眼掃過去知道哪幾張還沒請款。
              **字樣由後端給**,`NONE` 是空字串,那一格就什麼都不顯示。 */}
          {quote.billing_label ? (
            <Badge tone={quote.billing_state === "REQUESTED" ? "warn" : "ok"}>
              {quote.billing_label}
            </Badge>
          ) : null}
          {/* 收款標籤(業主 2026-09-30):只有老闆與財務拿得到值,其他人是 null。
              顏色之外一定有字 —— 狀態不可以只靠顏色。 */}
          {quote.collection ? (
            <Badge tone={COLLECTION[quote.collection].tone}>{COLLECTION[quote.collection].label}</Badge>
          ) : null}
        </span>
      );
    case "subtotal":
      return formatMoney(quote.subtotal);
    case "tax_amount":
      return formatMoney(quote.tax_amount);
    case "total":
      return formatMoney(quote.total);
    case "version":
      return quote.version ?? "";
    case "created_by":
      return quote.created_by_name ?? "";
    case "updated_at":
      return (quote.updated_at ?? "").slice(0, 10);
    default:
      return "";
  }
}

/** 這一格畫出來是不是一片空白。空白的格子當不了開單的入口。 */
function isBlank(content: ReactNode): boolean {
  return content === null || content === undefined || (typeof content === "string" && content.trim() === "");
}

/** 漏斗選項裡的一個客戶或一位同仁。 */
interface IdNameOption {
  id: number;
  name: string;
}

/** 漏斗選項裡的一個狀態。值是識別字,標籤是給人看的字。 */
interface ValueLabelOption {
  value: string;
  label: string;
}

/**
 * `GET /api/quotes/filter-options` 的回應。報價日是區間,不需要選項。
 *
 * 客戶與同仁的形狀不一樣,而且是刻意的:同仁是帳號,認 id;**客戶認的是
 * 列表那一欄實際顯示的名字**。用「新增報價單」表單開出來的單只有打字打上去的
 * 客戶名稱、沒有公司 id,以 id 當選項的話,系統裡最常見的那一種單既列不進
 * 漏斗、也篩不出來 —— 漏斗與列表看的必須是同一個值。
 */
interface QuoteFilterOptions {
  clients: ValueLabelOption[];
  owners: IdNameOption[];
  statuses: ValueLabelOption[];
}

const NO_OPTIONS: QuoteFilterOptions = { clients: [], owners: [], statuses: [] };

/** 後端回了非預期的形狀時,漏斗少幾個選項無妨,但清單不可以整個炸掉。 */
function readOptions(raw: QuoteFilterOptions | null | undefined): QuoteFilterOptions {
  if (!raw) return NO_OPTIONS;
  return {
    clients: Array.isArray(raw.clients) ? raw.clients : [],
    owners: Array.isArray(raw.owners) ? raw.owners : [],
    statuses: Array.isArray(raw.statuses) ? raw.statuses : [],
  };
}

/** 勾選的多個值串成一個參數。空字串會被 buildQuery 略過,也就是不帶這個條件 ——
    帶一個空的上去,後端會去找一個名字是空字串的東西,畫面一筆都不剩。 */
function commaJoin(values: string[]): string {
  return values.join(",");
}

export interface QuoteListProps {
  /** 開啟某一單。沒接上路由時退回整頁跳轉,前端掛在 /quote/ 底下。 */
  onOpen?: (quoteId: number) => void;
  /** 建立新的一單。同樣在沒接上路由時退回整頁跳轉。 */
  onCreate?: () => void;
  /** 去封存區。同樣在沒接上路由時退回整頁跳轉。 */
  onOpenArchive?: () => void;
  /** 封存成功之後要去的地方(封存區那一類的網址);沒給就整頁換過去。 */
  onArchived?: (url: string) => void;
}

export function QuoteList({ onOpen, onCreate, onOpenArchive, onArchived }: QuoteListProps) {
  const { user } = useAuth();
  const toast = useToast();
  /**
   * 財務對報價單唯讀(設計規格 §2.1)。看得到列表與金額,但開不了新單、
   * 也封存不了 —— 兩件事後端都是 `require_business`,這裡擋的是「按了才知道」。
   *
   * **只有在知道你是誰、而且你動不了的時候才切唯讀。** `user` 是 null 只發生在
   * 單獨 render 這個元件的測試裡(真實動線在 `RequireAuth` 之後),那時候把
   * 建單入口藏掉,等於逼每個列表測試都先假造一份登入狀態。
   *
   * 角色判斷一律走 `canWriteQuotes`,不在這裡寫 `role === "FINANCE"` ——
   * 那種字串比對散出去之後,第 15 輪加財務頁面時一定會漏掉其中幾處。
   */
  const readOnly = user !== null && !canWriteQuotes(user.role);
  // --------------------------------------------------------------------------
  // 漏斗與搜尋條件寫進網址
  // --------------------------------------------------------------------------
  // 業主 2026-09-16 回報:「點進一張單再返回,篩選條件會歸零」。
  //
  // 條件只活在元件的 state 裡的話,離開列表就把 QuoteList 卸載了,
  // 返回時全部重來 —— 而「篩到某個客戶 → 點進一張單 → 返回 → 再點下一張」
  // 正是這個功能最主要的用法。
  //
  // 寫進網址而不是 sessionStorage:網址還順便讓那一份篩選**貼得給別人**,
  // 而且重新整理也留得住。用 `replace` 不是 `push` —— 每打一個字就多一筆
  // 瀏覽記錄的話,按上一頁要按二十次才離得開這一頁。
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = useRef(searchParams);
  const fromUrl = (key: string): string[] => {
    const raw = initial.current.getAll(key);
    return raw.flatMap((one) => one.split(",")).filter(Boolean);
  };

  // keyword 是輸入框的即時值,query 是真的送出去的值。
  const [keyword, setKeyword] = useState(() => initial.current.get("q") ?? "");
  const [query, setQuery] = useState(() => initial.current.get("q") ?? "");
  const [data, setData] = useState<Paginated<QuoteSummary> | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [columns, setColumns] = useState<string[]>(FALLBACK_COLUMNS);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [columnsReady, setColumnsReady] = useState(false);
  // 分類:存著的勾選與這個人可勾的清單(後端給的)。讀到之前不撈列表 ——
  // 否則會先撈一次沒篩的、再撈一次篩過的,畫面閃一下全部的單。
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryLabels, setCategoryLabels] = useState<Record<string, string>>({});
  const [categoriesReady, setCategoriesReady] = useState(false);
  // 封存成功之後要重撈一次清單。單純改這個數字,讓底下那支 effect 再跑一遍 ——
  // 比在原地把那一列從 state 裡挑掉可靠:total 與分頁也一起跟著後端走。
  const [reloadToken, setReloadToken] = useState(0);
  // 封存掉一列之後,焦點交給右上角的「封存區」。那一列連同它的 ⋮ 會消失,
  // 焦點於是掉回 <body>,鍵盤使用者下一次按 Tab 要從整份文件最上面重來;
  // 而剛封完一張單,最可能想去的下一站就是封存區。
  const archiveEntryRef = useRef<HTMLButtonElement | null>(null);

  // 四個漏斗目前的選擇。客戶與同仁存的是 id 的字串,狀態存的是識別字。
  const [options, setOptions] = useState<QuoteFilterOptions>(NO_OPTIONS);
  const [clientPicks, setClientPicks] = useState<string[]>(() => fromUrl("client_name"));
  const [ownerPicks, setOwnerPicks] = useState<string[]>(() => fromUrl("owner_id"));
  const [statusPicks, setStatusPicks] = useState<string[]>(() => fromUrl("status"));
  const [range, setRange] = useState<DateRange>(() => ({
    from: initial.current.get("date_from") ?? "",
    to: initial.current.get("date_to") ?? "",
  }));

  const ownerParam = commaJoin(ownerPicks);
  const statusParam = commaJoin(statusPicks);
  const categoryParam = commaJoin(categories);
  const categoryActive = categoryParam !== "";

  // ── 分頁 ──
  // 頁碼與篩選一樣寫進網址:點進第 2 頁的某張單再返回,還要在第 2 頁。
  // 每頁幾筆是長久的偏好,記在這台瀏覽器上(Pagination 檔頭)。
  const [pageSize, savePageSize] = usePageSize("nebula.pageSize.quotes");
  const [paging, setPaging] = useState<{ key: string | null; page: number }>(() => ({
    key: null,
    page: Math.max(1, Math.floor(Number(initial.current.get("page"))) || 1),
  }));
  // **任何條件一變就回第 1 頁**,而且在 render 當下就換掉:用 effect 事後再改的話,
  // 會先拿舊頁碼配新條件撈一次 —— 篩到只剩 3 筆卻在要第 2 頁,畫面閃一下空清單。
  // 分類讀到之前不算數:它從空字串變成存著的值不是使用者改了條件,
  // 算進去的話,有存分類的人從網址帶著 ?page=2 回來會被打回第 1 頁。
  const filterKey = JSON.stringify([query, clientPicks, ownerParam, statusParam, range.from, range.to, categoryParam, pageSize]);
  let page = paging.page;
  if (categoriesReady && paging.key !== filterKey) {
    page = paging.key === null ? paging.page : 1;
    setPaging({ key: filterKey, page });
  }
  const setPage = (next: number) => setPaging((prev) => ({ ...prev, page: next }));

  // 條件變了就把網址改掉。**只寫有值的那幾個** —— 空參數留在網址上,
  // 複製給別人的那一份會長得很嚇人,而且看不出到底篩了什麼。
  useEffect(() => {
    const next = new URLSearchParams();
    if (query) next.set("q", query);
    for (const name of clientPicks) next.append("client_name", name);
    if (ownerParam) next.set("owner_id", ownerParam);
    if (statusParam) next.set("status", statusParam);
    if (range.from) next.set("date_from", range.from);
    if (range.to) next.set("date_to", range.to);
    if (page > 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [query, clientPicks, ownerParam, statusParam, range.from, range.to, page, setSearchParams]);
  const filtering =
    clientPicks.length > 0 || ownerParam !== "" || statusParam !== "" || range.from !== "" || range.to !== "";

  /** 把所有漏斗一次收乾淨。 */
  const clearFilters = () => {
    setClientPicks([]);
    setOwnerPicks([]);
    setStatusPicks([]);
    setRange({ from: "", to: "" });
  };

  /** 把存著的分類清空(存回帳號)。只有空清單那一句旁邊的「清除分類」用。 */
  const clearCategories = () => {
    api
      .put<ListCategorySettings>("/users/me/list-categories", { quote_list_categories: [] })
      .then((updated) => {
        const next = updated?.quote_list_categories ?? [];
        setCategories(next);
        announceCategories(next);
      })
      .catch((error) => toast.push(error instanceof Error ? error.message : "儲存失敗", "error"));
  };

  // 漏斗的選項只在進到這一頁時讀一次。要不到就是沒有漏斗可點,
  // **列表本身照常顯示** —— 篩不了是小事,看不到自己有哪些單子是大事。
  useEffect(() => {
    let cancelled = false;
    api
      .get<QuoteFilterOptions>("/quotes/filter-options")
      .then((result) => {
        if (!cancelled) setOptions(readOptions(result));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // 漏斗浮層裡的選項。順序跟著後端給的走,HeaderFilter 回傳的勾選也照這個順序排 ——
  // 同一組勾選每次都組出同一個字串,送出去的網址才不會因為點擊先後而長得不一樣。
  const clientOptions = useMemo(
    () => options.clients.map((row) => ({ value: row.value, label: row.label })),
    [options.clients],
  );
  const ownerOptions = useMemo(
    () => options.owners.map((row) => ({ value: String(row.id), label: row.name })),
    [options.owners],
  );
  const statusOptions = useMemo(
    () => options.statuses.map((row) => ({ value: row.value, label: row.label })),
    [options.statuses],
  );

  useEffect(() => {
    if (keyword === query) return;
    const timer = window.setTimeout(() => setQuery(keyword), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [keyword, query]);

  // 欄位設定只在進到這一頁時讀一次。讀不到就用退路畫,**列表本身不該因此打不開** ——
  // 老闆的顯示偏好壞了是小事,看不到自己有哪些單子是大事。
  useEffect(() => {
    let cancelled = false;
    api
      .get<ListColumnSettings>("/users/me/list-columns")
      .then((settings) => {
        if (cancelled) return;
        const available = settings?.available_columns ?? {};
        setLabels(available);
        setColumns(normalizeColumns(settings?.quote_list_columns, available));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setColumnsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 左側二級選單(全部 / 已結案 / 未結案)存了新的分類:跟著換,列表當場重撈。
  useEffect(() => {
    const onChanged = (event: Event) => setCategories((event as CustomEvent<string[]>).detail ?? []);
    window.addEventListener(QUOTE_CATEGORIES_CHANGED, onChanged);
    return () => window.removeEventListener(QUOTE_CATEGORIES_CHANGED, onChanged);
  }, []);

  // 分類設定只在進到這一頁時讀一次。讀不到就當作沒勾、也不畫那顆按鈕,
  // **列表本身照常**。
  useEffect(() => {
    let cancelled = false;
    api
      .get<ListCategorySettings>("/users/me/list-categories")
      .then((settings) => {
        if (cancelled) return;
        setCategoryLabels(settings?.available_categories ?? {});
        setCategories(settings?.quote_list_categories ?? []);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setCategoriesReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!categoriesReady) return;
    let cancelled = false;
    setLoading(true);
    api
      .get<Paginated<QuoteSummary>>("/quotes", {
        q: query,
        // 空字串一律被 buildQuery 略過,所以「沒勾任何條件」是真的不帶那個參數。
        status: statusParam,
        // 客戶送的是名字,而且**重複帶、不串逗號**:客戶名稱本來就可能有逗號
        // (Acme, Inc.),串起來後端一切,那家客戶就永遠篩不到。
        client_name: clientPicks,
        owner_id: ownerParam,
        date_from: range.from,
        date_to: range.to,
        category: categoryParam,
        page,
        size: pageSize,
      })
      .then((result) => {
        if (cancelled) return;
        // 頁碼超過最後一頁(封存掉最後一頁唯一那張、或網址上帶著舊頁碼):
        // 退回最後一頁再撈一次,不要秀一張空表說「沒有符合的報價單」。
        const lastPage = pageCount(result?.total ?? 0, pageSize);
        if (page > lastPage && (result?.total ?? 0) > 0) {
          setPage(lastPage);
          return;
        }
        setData(result);
        setFailed(false);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setFailed(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [categoriesReady, query, statusParam, clientPicks, ownerParam, range.from, range.to, categoryParam, page, pageSize, reloadToken]);

  const open = (quoteId: number) => {
    if (onOpen) {
      onOpen(quoteId);
      return;
    }
    if (typeof window !== "undefined") window.location.assign(`/quote/quotes/${quoteId}`);
  };

  const create = () => {
    if (onCreate) {
      onCreate();
      return;
    }
    if (typeof window !== "undefined") window.location.assign("/quote/quotes/new");
  };

  const openArchive = () => {
    if (onOpenArchive) {
      onOpenArchive();
      return;
    }
    if (typeof window !== "undefined") window.location.assign("/quote/quotes/archive");
  };

  /**
   * 把一張單收進封存區。
   *
   * 成功之後**重撈清單**而不是在原地把那一列拿掉:排除已封存的單是後端的條件
   * (規格 §6),`total` 與分頁也都由它算 —— 前端自己挑掉一列,那個數字就會少一。
   *
   * 失敗只吐一句話,清單原封不動:那一列還在,使用者看得到自己要再試一次的是哪一張。
   */
  const archive = async (quote: QuoteSummary) => {
    try {
      await api.post(`/quotes/${quote.id}/archive`);
      toast.push(`${quote.quote_no} 已封存`, "success");
      setReloadToken((prev) => prev + 1);
      // 封存完直接去封存區裡這張單那一類(業主 2026-10-16)
      if (onArchived) onArchived(archiveTabUrl(quote.status));
      else if (typeof window !== "undefined") window.location.assign(`/quote${archiveTabUrl(quote.status)}`);
    } catch (error) {
      toast.push(error instanceof Error ? error.message : "封存失敗", "error");
    }
  };


  // 後端萬一回了非預期的形狀,也不能讓清單整個炸掉。
  const items: QuoteSummary[] = data && Array.isArray(data.items) ? data.items : [];

  // 點得開的那一格。單號沒被勾起來時往下找「一定有值」的欄(專案名稱是必填),
  // 都沒有才退回第一欄 —— 否則老闆把單號拿掉之後,整張表就沒有任何一格打得開報價單了。
  // 最後那道 FALLBACK 只在 columns 反常地空掉時用得到,一顆按鈕都沒有的表比什麼都糟。
  const openColumn =
    OPEN_COLUMN_PREFERENCE.find((key) => columns.includes(key)) ?? columns[0] ?? FALLBACK_COLUMNS[0];

  // 列尾 ⋮ 掛在最後一欄的內容後面,不另開一欄(見檔頭)。
  // 一個動作都沒有的角色(財務)整顆 ⋮ 不畫 —— 開起來是空的選單,
  // 鍵盤使用者還得先 Tab 進去才發現裡面沒東西。
  const menuColumn = readOnly || columns.length === 0 ? null : columns[columns.length - 1];

  /** 這一欄的表頭要不要掛漏斗,掛哪一種。沒有選項可選時不掛 ——
      一個空浮層只會浪費使用者一次點擊。 */
  const filterFor = (key: string): ReactNode => {
    const label = labels[key] ?? key;
    if (key === "client" && clientOptions.length > 0) {
      return (
        <HeaderFilter
          label={label}
          options={clientOptions}
          selected={clientPicks}
          onChange={setClientPicks}
        />
      );
    }
    if (key === "owner" && ownerOptions.length > 0) {
      return (
        <HeaderFilter
          label={label}
          options={ownerOptions}
          selected={ownerPicks}
          onChange={setOwnerPicks}
        />
      );
    }
    if (key === "status" && statusOptions.length > 0) {
      return (
        <HeaderFilter
          label={label}
          options={statusOptions}
          selected={statusPicks}
          onChange={setStatusPicks}
        />
      );
    }
    if (key === "quote_date") {
      // 日期不需要選項清單,所以不看 filter-options 回了什麼。
      return <DateRangeFilter label={label} value={range} onChange={setRange} />;
    }
    return null;
  };

  // 勾了條件卻一筆都沒中的時候,表格**還是要在**:漏斗長在表頭上,整張表收掉
  // 就等於把唯一能取消篩選的入口一起收掉,使用者只剩重新整理這條路。
  const showTable = columnsReady && !failed && (items.length > 0 || filtering || categoryActive);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {/* 搜尋在左、新增在右上。建單的入口不能只藏在 Ctrl+K 與網址裡。 */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[12rem] flex-1 items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 focus-within:border-[var(--accent)]">
          <Search aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
          <input
            type="search"
            value={keyword}
            aria-label="搜尋報價單"
            placeholder="搜尋單號、專案名稱、客戶,或單子裡的明細"
            onChange={(event) => setKeyword(event.target.value)}
            // 不要加 outline-none:焦點環是鍵盤使用者唯一看得出「我在哪」的線索。
            className="h-9 w-full min-w-0 border-0 bg-transparent text-sm text-[var(--text)] placeholder:text-[var(--text-3)]"
          />
        </div>
        {/* 一次收乾淨的入口。勾了三個漏斗之後要一個一個點回去很煩,而勾在
            已經被隱藏起來的那一欄上的條件,更是只剩這一顆按鈕解得掉。 */}
        {filtering ? (
          <Button icon={<FilterX aria-hidden="true" size={16} />} onClick={clearFilters}>
            清除篩選
          </Button>
        ) : null}
        {/* 欄位設定就在它作用的這一頁上(業主:「搬到報價單列表頁面」)。
            每個人一份,存在自己的帳號上(業主 2026-09-25)。 */}
        {/* 分類緊貼在「顯示欄位」前面:都是「這一頁要看什麼」,而且都存在自己的帳號上。 */}
        <CategoryPicker selected={categories} available={categoryLabels} onSaved={(next) => {
          setCategories(next);
          announceCategories(next);
        }} />
        <ColumnPicker columns={columns} available={labels} onSaved={setColumns} />
        {/* 封存區在報價單底下的一層(業主:「報價單>封存」),入口就在這裡,
            不是側邊欄多一個項目 —— 側邊欄已經七項了。 */}
        <Button ref={archiveEntryRef} icon={<Archive aria-hidden="true" size={16} />} onClick={openArchive}>
          封存區
        </Button>
        {/* 建單是業務動作,財務按不得(後端 `require_business`)。
            這一顆**藏起來而不是反灰**:它不是「這一列做不得」,是這個角色
            從頭到尾都不開單 —— 一顆永遠灰著的主要按鈕擺在右上角,
            只會讓人每次進來都再試一次。為什麼沒有它,由下面那一句說。 */}
        {readOnly ? null : (
          <Button variant="primary" icon={<FilePlus2 aria-hidden="true" size={16} />} onClick={create}>
            新增報價單
          </Button>
        )}
      </div>

      {/* 少了建單入口與列尾 ⋮,一定要有人說一句為什麼 —— 不然使用者只會以為
          這一版把按鈕弄丟了。權限不可以只靠「那裡本來有東西現在沒有」來表達。 */}
      {readOnly ? (
        <p role="status" className="m-0 text-sm text-[var(--text-2)]">
          {`${QUOTE_IS_READ_ONLY_FOR_ROLE}:報價單看得到內容與金額,但不能建立、修改或封存。`}
        </p>
      ) : null}

      {(loading || !columnsReady) && data === null ? (
        <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
      ) : null}

      {failed ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          清單載入失敗,請稍後再試。
        </p>
      ) : null}

      {/* 篩不到東西與「一張單都還沒有」是兩件事。混成同一句「還沒有報價單」,
          使用者會以為資料不見了,而不是自己勾的條件太窄。 */}
      {!loading && !failed && items.length === 0 && (filtering || categoryActive) ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--text-2)]">
          <p className="m-0">
            {categoryActive
              ? `沒有符合目前篩選條件的報價單(${categoryLabel(categories, categoryLabels)})。`
              : "沒有符合目前篩選條件的報價單。"}
            {filtering ? "可以放寬條件,或按「清除篩選」。" : "可以換一個分類,或清除分類看全部。"}
          </p>
          {/* 分類是存著的偏好,「清除篩選」不動它,所以這裡另給一個看得見的出口。 */}
          {categoryActive ? (
            <Button size="sm" onClick={clearCategories}>
              清除分類
            </Button>
          ) : null}
        </div>
      ) : null}

      {!loading && !failed && items.length === 0 && !filtering && !categoryActive ? (
        <EmptyState
          title="還沒有報價單"
          // 文案說得出口的動作,旁邊就要有按鈕 —— 反過來說,按不得的動作
          // 就不要寫進文案裡:叫財務「建立第一份報價單」而旁邊沒有按鈕,
          // 等於請他去找一個不存在的入口。
          description={
            readOnly
              ? "目前一張報價單都沒有。等同仁開了單,這裡就看得到內容與金額。"
              : "建立第一份報價單,或先套用「會議活動製作標準七類」範本。"
          }
          action={
            readOnly ? undefined : (
              <Button variant="primary" icon={<FilePlus2 aria-hidden="true" size={16} />} onClick={create}>
                建立第一份報價單
              </Button>
            )
          }
        />
      ) : null}

      {showTable ? (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table
            aria-label="報價單清單"
            // 最小寬度跟著欄數走:只留兩欄時不該硬撐出一張橫捲的空表。
            style={{ minWidth: `${Math.max(18, columns.length * 7.5)}rem` }}
            className="w-full border-collapse text-left"
          >
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                {columns.map((key) => {
                  // 漏斗就在它作用的那一欄上面(業主:「應該是要直接在表頭
                  // 有一下漏斗」),名稱一樣讀後端那一份標題,不另外命名。
                  const funnel = filterFor(key);
                  return (
                    <th
                      key={key}
                      scope="col"
                      // **標題一律不換行。** 表格是 table-layout: auto,欄寬由內容
                      // 分配 —— 標題長的那幾欄吃掉空間,「狀態」這種短標題就被壓到
                      // 只剩一個字寬然後折成兩行(業主 2026-09-17:「被壓縮 很醜
                      // 不專業」)。擠不下就讓整張表橫捲(外層已經有 overflow-x-auto),
                      // 不要把字折斷 —— 與明細表格同一個處理方式。
                      className={`whitespace-nowrap px-3 py-2 ${MONEY_COLUMNS.has(key) ? "text-right" : ""}`}
                    >
                      {funnel === null ? (
                        (labels[key] ?? key)
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          {labels[key] ?? key}
                          {funnel}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {items.map((quote) => (
                <tr key={quote.id} className="border-t border-[var(--border)]">
                  {columns.map((key) => {
                    const content = cellOf(key, quote);
                    const classes = [
                      "px-3 py-2 text-sm",
                      // 日期、單號與金額折成兩行更難讀,而且會把整列撐高。
                      // 文字欄(標題、客戶)不加:那幾欄本來就該換行。
                      TABULAR_COLUMNS.has(key) ? "tabular whitespace-nowrap" : "",
                      MONEY_COLUMNS.has(key) ? "text-right" : "",
                      key === "title" ? "text-[var(--text)]" : "text-[var(--text-2)]",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    const cell =
                      key === openColumn ? (
                        <button
                          type="button"
                          onClick={() => open(quote.id)}
                          // 名稱不跟著那一格的內容走。第一欄可能是還沒指定的客戶,
                          // 內容一空就會是一顆沒有可及名稱的按鈕:報讀器只唸得出「按鈕」,
                          // 畫面上也只剩一塊點不到的空白 —— 那一列就再也打不開了。
                          aria-label={`開啟報價單 ${quote.quote_no}`}
                          className="rounded-[var(--radius)] text-sm font-medium text-[var(--accent)] hover:text-[var(--accent-hover)] hover:underline"
                        >
                          {isBlank(content) ? quote.quote_no || "—" : content}
                        </button>
                      ) : (
                        content
                      );
                    return (
                      <td key={key} className={classes}>
                        {key === menuColumn ? (
                          <span
                            className={
                              "flex items-center gap-1 " +
                              (MONEY_COLUMNS.has(key) ? "justify-end" : "justify-between")
                            }
                          >
                            <span className="min-w-0">{cell}</span>
                            {/* 直接一顆封存圖示,不收在 ⋮ 裡(業主 2026-10-16)。名稱帶單號,不用列號:
                                排序或篩選換了之後,「第 3 列」指的已經不是同一張單了。
                                已寄出的封存不了,按鈕反灰並說出原因。 */}
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`封存 ${quote.quote_no}`}
                              title={canArchive(quote.status) ? `封存 ${quote.quote_no}` : ARCHIVE_REFUSED}
                              disabled={!canArchive(quote.status)}
                              icon={<Archive aria-hidden="true" size={16} />}
                              onClick={() => void archive(quote)}
                            />
                          </span>
                        ) : (
                          cell
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {data && items.length > 0 ? (
        <Pagination
          total={data.total}
          page={page}
          size={pageSize}
          onPageChange={setPage}
          onSizeChange={savePageSize}
        />
      ) : null}
    </div>
  );
}
